import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@context/AuthContext';
import { authService } from '@services/auth/authService';
import Button from '@components/Button/Button';
import Input from '@components/Form/Input/Input';
import { LogOut } from 'lucide-react';

const StudentChangePassword: React.FC = () => {
    const { t } = useTranslation('students');
    const { register, handleSubmit, watch, formState: { errors } } = useForm();
    const [isLoading, setIsLoading] = useState(false);
    const [serverError, setServerError] = useState('');
    const navigate = useNavigate();
    const { login, logout } = useAuth();

    const newPassword = watch('new_password');

    const onSubmit = async (data: any) => {
        try {
            setIsLoading(true);
            setServerError('');
            await authService.studentChangePassword(data.new_password);
            
            // Re-fetch profile to update user state
            const token = localStorage.getItem('studentAccessToken');
            if (token) {
                await login(token);
            }
            
            navigate('/student/dashboard');
        } catch (error: any) {
            setServerError(error?.response?.data?.message || t('portal.changePassword.updateFail'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    return (
        <div className="min-h-screen bg-blue-bg flex flex-col items-center justify-center p-4">
            <button
                onClick={handleLogout}
                className="absolute top-6 right-6 flex items-center gap-2 text-slate-body hover:text-navy hover:bg-white px-3 py-1.5 rounded-full transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2"
            >
                <LogOut size={20} />
                <span>{t('portal.changePassword.logout')}</span>
            </button>

            <div className="bg-white rounded-2xl shadow-card w-full max-w-md p-8">
                <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-yellow-100 text-yellow-600 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                    </div>
                    <h1 className="text-2xl font-bold text-navy mb-2">{t('portal.changePassword.title')}</h1>
                    <p className="text-slate-body">{t('portal.changePassword.subtitle')}</p>
                </div>

                {serverError && (
                    <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-xl text-sm text-center">
                        {serverError}
                    </div>
                )}

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                    <Input
                        label={t('portal.changePassword.newPwdLabel')}
                        type="password"
                        placeholder={t('portal.changePassword.newPwdPlaceholder')}
                        registration={register('new_password', {
                            required: t('portal.changePassword.newPwdRequired'),
                            minLength: { value: 6, message: t('portal.changePassword.newPwdMinLength') }
                        })}
                        error={errors.new_password?.message as string}
                    />

                    <Input
                        label={t('portal.changePassword.confirmPwdLabel')}
                        type="password"
                        placeholder={t('portal.changePassword.confirmPwdPlaceholder')}
                        registration={register('confirm_password', {
                            required: t('portal.changePassword.confirmPwdRequired'),
                            validate: (val) => val === newPassword || t('portal.changePassword.pwdMismatch')
                        })}
                        error={errors.confirm_password?.message as string}
                    />

                    <Button
                        type="submit"
                        variant="primary"
                        className="w-full !py-3 !rounded-full hover:-translate-y-0.5 transition-all duration-300"
                        isLoading={isLoading}
                    >
                        {t('portal.changePassword.updateBtn')}
                    </Button>
                </form>
            </div>
        </div>
    );
};

export default StudentChangePassword;
